import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useResidentPopulation,
} from '@src/modules/performance/context/reports-api-hooks';
import Chart from 'react-apexcharts';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Population',
  content: [
    {
      title: 'Resident Population',
      description:
  <Text fontSize="inherit" mb={2}>
    The estimated resident population at 30 June each year indexed to 1996. Subnational population estimates at 30 June each year were obtained by updating the census night base population of each area for births, deaths, and net migration.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Stats NZ
    {' '}
    <Link href="https://www.stats.govt.nz/topics/population-estimates-and-projections/" isExternal>Population Estimates.</Link>
    {' '}
    Data sourced from
    {' '}
    <Link href="https://www.stats.govt.nz/tools/" isExternal>Stats NZ web tool</Link>
    {' '}
    and the
    {' '}
    <Link href="http://webrear.mbie.govt.nz/theme/population-estimates/map/timeseries/2018/new-zealand?left-transform=indexed&right-transform=indexed" isExternal>MBIE Regional Economic Activity Tool.</Link>
  </Text>,
    },
  ],
};

function TerritorialActivityPopulation() {
  const { data: residentPopulation, isLoading } = useResidentPopulation();
  const lockYear = residentPopulation?.[0]?.LockYear || null;

  const chartData = [
    {
      name: 'Population',
      type: 'line',
      data: residentPopulation?.map((item) => ({
        x: item.year,
        y: item.value,
      })),
      color: CHART_COLOURS.darkblue,
    },
    {
      name: 'Annual Change',
      type: 'bar',
      data: residentPopulation?.map((item) => ({
        x: item.year,
        y: item.population_change,
      })),
      color: CHART_COLOURS.lightblue,
    },
  ];

  return (
    <ReportSection
      title="Population"
      isLoading={isLoading || !residentPopulation}
      informationSource={informationSource}
      lockYear={lockYear}
    >
      <Chart
        options={
          {
            title: {
              text: 'Resident Population',
            },
            chart: {
              animations: {
                enabled: false,
              },
              events: {
                mounted(chartContext) {
                  chartContext.toggleSeries('Annual Change');
                },
              },
            },
            xaxis: {
              labels: {
                offsetY: 5,
                rotate: -45,
                rotateAlways: true,
              },
              tickPlacement: 'between',
              tooltip: {
                enabled: false,
              },
            },
            yaxis: [
              {
                seriesName: 'Population',
                opposite: true,
                axisTicks: {
                  show: true,
                },
                title: {
                  text: 'Population',
                },
                tooltip: {
                  enabled: false,
                },
                labels: {
                  formatter(y) {
                    return y != null
                      ? new Intl.NumberFormat('en', {
                        notation: 'compact',
                        compactDisplay: 'short',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }).format(y)
                      : '';
                  },
                },
              },
              {
                seriesName: 'Annual Change',
                axisTicks: {
                  show: true,
                },
                title: {
                  text: 'Annual Change',
                },
                show: true,
                labels: {
                  formatter(y) {
                    return y != null
                      ? new Intl.NumberFormat('en', {
                        notation: 'compact',
                        compactDisplay: 'short',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 1,
                      }).format(y)
                      : '';
                  },
                },
              },
            ],
            grid: {
              padding: {
                left: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            tooltip: {
              shared: true,
              followCursor: true,
            },
          }
        }
        series={chartData}
        type="line"
        width="350"
        height="280"
      />
    </ReportSection>
  );
}

export default TerritorialActivityPopulation;
